import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MonoIcon } from '@/components/ui/MonoIcon';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle
} from '@/components/ui/dialog';
import { workspaceApi } from '@/lib/api/workspace/workspaceApi';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { KnowledgeBaseEntry } from 'dico-types';

type ViewMode = 'card' | 'list';

type EditMode = 'create' | 'edit';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function compactContent(value: string): string {
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (normalized.length <= 180) return normalized;
  return `${normalized.slice(0, 177)}...`;
}

export function KnowledgeBasePage() {
  const { id: workspaceId } = useParams<{ id: string }>();

  const [entries, setEntries] = useState<KnowledgeBaseEntry[]>([]);
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [viewMode, setViewMode] = useState<ViewMode>('card');
  const [sortDirection, setSortDirection] = useState<'desc' | 'asc'>('desc');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState<EditMode>('edit');
  const [activeEntry, setActiveEntry] = useState<KnowledgeBaseEntry | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadEntries = useCallback(async () => {
    if (!workspaceId) return;
    setLoadState('loading');
    try {
      const res = await workspaceApi.getKnowledgeBaseEntries(workspaceId);
      if (!res.success) {
        throw new Error(res.error ?? 'Failed to load entries');
      }
      setEntries(res.data ?? []);
      setLoadState('loaded');
    } catch {
      setLoadState('error');
      toast.error('Failed to load knowledge base entries.');
    }
  }, [workspaceId]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const sortedEntries = useMemo(() => {
    return [...entries].sort((a, b) => {
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      return sortDirection === 'desc' ? bTime - aTime : aTime - bTime;
    });
  }, [entries, sortDirection]);

  const openCreateDialog = () => {
    setEditMode('create');
    setActiveEntry(null);
    setTitle('');
    setContent('');
    setTags('');
    setIsDialogOpen(true);
  };

  const openEditDialog = (entry: KnowledgeBaseEntry) => {
    setEditMode('edit');
    setActiveEntry(entry);
    setTitle(entry.title);
    setContent(entry.content);
    setTags(entry.tags.join(', '));
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!workspaceId) return;
    const trimmedTitle = title.trim();
    const trimmedContent = content.trim();
    if (!trimmedTitle || !trimmedContent) return;

    setSaving(true);
    try {
      const tagArray = tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);

      if (editMode === 'create') {
        const generatedKey =
          trimmedTitle
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '')
            .slice(0, 64) || `entry_${Date.now()}`;

        const res = await workspaceApi.createKnowledgeBaseEntry(workspaceId, {
          key: generatedKey,
          title: trimmedTitle,
          content: trimmedContent,
          tags: tagArray
        });

        if (!res.success || !res.data) {
          toast.error(res.error ?? 'Failed to create entry');
          return;
        }

        setEntries((prev) => [res.data, ...prev]);
        toast.success('Entry created');
      } else {
        if (!activeEntry) return;
        const res = await workspaceApi.updateKnowledgeBaseEntry(workspaceId, activeEntry.key, {
          title: trimmedTitle,
          content: trimmedContent,
          tags: tagArray
        });

        if (!res.success || !res.data) {
          toast.error(res.error ?? 'Failed to save entry');
          return;
        }

        setEntries((prev) => prev.map((entry) => (entry.id === res.data?.id ? res.data : entry)));
        toast.success('Entry saved');
      }

      setIsDialogOpen(false);
    } catch {
      toast.error(editMode === 'create' ? 'Failed to create entry' : 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!workspaceId || !activeEntry) return;
    setDeleting(true);
    try {
      const res = await workspaceApi.deleteKnowledgeBaseEntry(workspaceId, activeEntry.key);
      if (!res.success) {
        toast.error(res.error ?? 'Failed to delete entry');
        return;
      }
      setEntries((prev) => prev.filter((entry) => entry.key !== activeEntry.key));
      setIsDialogOpen(false);
      toast.success('Entry deleted');
    } catch {
      toast.error('Failed to delete entry');
    } finally {
      setDeleting(false);
    }
  };

  if (!workspaceId) {
    return (
      <div className="text-muted-foreground flex min-h-[40vh] items-center justify-center">
        Workspace not found.
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-border w-3xl mx-auto flex h-12 items-center justify-between border-b px-4 xl:border-x">
        <div className="flex items-center gap-2">
          <h1 className="text-foreground text-sm font-medium tracking-tight">Knowledge Base</h1>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground h-7 gap-1.5"
            onClick={() => setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'))}
          >
            <MonoIcon type="Sort" />
            Sorted by Creation date
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <div className="border-border bg-muted flex items-center rounded-lg border">
            <Button
              variant={viewMode === 'card' ? 'outline' : 'ghost'}
              size="icon-sm"
              onClick={() => setViewMode('card')}
              aria-label="Card view"
            >
              <MonoIcon type="Grid" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'outline' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={cn(
                'h-7 w-7 p-0',
                viewMode === 'list'
                  ? 'bg-background text-foreground shadow-xs'
                  : 'text-muted-foreground'
              )}
              aria-label="List view"
            >
              <MonoIcon type="List" />
            </Button>
          </div>

          <Button className="gap-1.5" onClick={openCreateDialog}>
            <MonoIcon type="Plus" />
            New note
          </Button>
        </div>
      </header>

      <main className="w-3xl mx-auto flex-1 p-6 xl:border-x">
        <div className="w-full">
          {loadState === 'loading' && (
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <MonoIcon type="Loader2" className="animate-spin" />
              Loading notes...
            </div>
          )}

          {loadState === 'loaded' && sortedEntries.length === 0 && (
            <div className="border-border bg-card rounded-xl border border-dashed p-10 text-center">
              <MonoIcon type="FileText" className="text-muted-foreground mx-auto mb-3" />
              <p className="text-foreground text-sm font-medium">No notes yet</p>
              <p className="text-muted-foreground mt-1 text-xs">
                Create your first knowledge base note to capture context for your workspace.
              </p>
            </div>
          )}

          {loadState === 'loaded' && sortedEntries.length > 0 && viewMode === 'card' && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-2">
              {sortedEntries.map((entry) => (
                <div
                  key={entry.id}
                  onClick={() => openEditDialog(entry)}
                  className="border-border hover:bg-muted shadow-xs flex min-h-36 flex-col rounded-lg border text-left transition-colors"
                >
                  <div className="flex items-start justify-between gap-2 p-2 px-4">
                    <h3 className="text-foreground line-clamp-2 text-sm font-medium">
                      {entry.title}
                    </h3>
                  </div>

                  <div className="bg-card shadow-xs m-1 mt-0 flex flex-1 flex-col rounded-md border p-2">
                    <div className="line-clamp-4 p-2 text-sm">{compactContent(entry.content)}</div>

                    <div className="mt-auto flex items-baseline justify-between gap-2">
                      <div className="flex items-center gap-1">
                        {entry.tags.length > 0 &&
                          entry.tags.map((tag) => (
                            <Button variant="outline" size="sm">
                              <MonoIcon type="Tag" />
                              {tag}
                            </Button>
                          ))}
                      </div>
                      <div className="text-foreground text-muted-foreground px-1 text-xs">
                        {formatDate(entry.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {loadState === 'loaded' && sortedEntries.length > 0 && viewMode === 'list' && (
            <div className="border-border shadow-xs overflow-hidden rounded-lg border">
              <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_140px] gap-4 px-4 py-2.5">
                <p className="text-muted-foreground text-xs font-medium">Note</p>
                <p className="text-muted-foreground text-xs font-medium">Record</p>
                <p className="text-muted-foreground text-xs font-medium">Created at</p>
              </div>
              <ul className="divide-border bg-card shadow-xs m-1 mt-0 divide-y rounded-md border">
                {sortedEntries.map((entry) => (
                  <li key={entry.id}>
                    <button
                      onClick={() => openEditDialog(entry)}
                      className="hover:bg-muted/40 grid w-full grid-cols-[minmax(0,2fr)_minmax(0,1fr)_140px] items-center gap-4 px-3 py-2 text-left transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-foreground truncate text-sm font-medium">
                          {entry.title}
                        </p>
                        <p className="text-muted-foreground truncate text-xs">{entry.key}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        {entry.tags.length > 0 &&
                          entry.tags.map((tag) => (
                            <Button variant="outline" size="sm">
                              <MonoIcon type="Tag" />
                              {tag}
                            </Button>
                          ))}
                      </div>
                      <p className="text-foreground text-xs">{formatDate(entry.createdAt)}</p>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </main>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogPortal>
          <DialogContent className="w-[900px] gap-3">
            <DialogHeader>
              <DialogTitle>{editMode === 'create' ? 'New note' : 'Edit note'}</DialogTitle>
              <DialogDescription>
                {editMode === 'create'
                  ? 'Create a new knowledge base note.'
                  : 'Update this note content used by the AI context.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-foreground text-sm font-medium">Title</label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-foreground text-sm font-medium">
                  Tags (comma-separated)
                </label>
                <Input
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="e.g. notes, process, support"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-foreground text-sm font-medium">Content</label>
                <Textarea
                  className="min-h-[260px]"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Write knowledge base content..."
                />
              </div>
            </div>

            <DialogFooter className="mt-1">
              {editMode === 'edit' && (
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="mr-auto gap-1.5"
                >
                  {deleting && <MonoIcon type="Loader2" className="animate-spin" />}
                  <MonoIcon type="Trash" />
                  Delete
                </Button>
              )}
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || !title.trim() || !content.trim()}>
                {saving && <MonoIcon type="Loader2" className="animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </div>
  );
}
